import { getRankConfig } from '@infinitro/config';
/** Number of rank levels (1–11). */
export const RANK_COUNT = 11;
const ROMAN_MAP = {
    1: 'I',
    2: 'II',
    3: 'III',
    4: 'IV',
    5: 'V'
};
const ROMAN_TO_NUM = {
    I: 1,
    II: 2,
    III: 3,
    IV: 4,
    V: 5
};
/** Rank config for the current theme. Uses theme rankConfig or default (numeric sublevels). */
export const getCurrentRankConfig = () => getRankConfig();
/**
 * Tier label without theme-specific names: numeric level index only.
 * Prefer API strings (`vipRank`, `vipLevels.name`) for user-facing copy.
 */
export const getRankTierDisplayName = (levelIndex, _config) => {
    if (levelIndex < 1 || levelIndex > RANK_COUNT)
        return '';
    return String(levelIndex);
};
/**
 * Full rank display from level/sublevel only (no planet/tier names from config).
 * Prefer API-provided rank strings in UI where available.
 */
export const formatRankWithSublevel = (levelIndex, sublevel, config) => {
    const c = config ?? getCurrentRankConfig();
    if (c.sublevelFormat === 'named_numeric' && levelIndex === RANK_COUNT) {
        return String(levelIndex);
    }
    const sub = formatSublevel(sublevel, c);
    if (c.sublevelFormat === 'none') {
        return String(levelIndex);
    }
    return sub ? `${levelIndex} ${sub}` : String(levelIndex);
};
/** Format sublevel 0–5 for display (roman, numeric, or '' when none). Uses current theme config. */
export const formatSublevel = (sublevel, config) => {
    const c = config ?? getCurrentRankConfig();
    switch (c.sublevelFormat) {
        case 'roman':
            return sublevel >= 1 && sublevel <= 5 ? (ROMAN_MAP[sublevel] ?? String(sublevel)) : '';
        case 'numeric':
            return sublevel >= 1 ? String(sublevel) : '';
        case 'named_numeric':
            return sublevel >= 0 && sublevel <= 5 ? String(sublevel + 1) : '';
        case 'none':
            return '';
        default:
            return sublevel >= 1 ? String(sublevel) : '';
    }
};
/** Parse sublevel from string (e.g. "gold III" -> 3, "3.2" -> 2). For 'roman' config, accepts I–V. */
export const parseSublevelFromString = (rankString, config) => {
    if (!rankString)
        return undefined;
    const s = rankString.trim();
    const c = config ?? getCurrentRankConfig();
    if (s.includes('.')) {
        const after = s.split('.')[1];
        const n = after != null ? parseInt(after, 10) : NaN;
        return Number.isNaN(n) ? undefined : n;
    }
    const part = s.split(/\s+/)[1];
    if (part == null)
        return undefined;
    if (c.sublevelFormat === 'roman') {
        const n = ROMAN_TO_NUM[part];
        return n !== undefined ? n : undefined;
    }
    if (c.sublevelFormat === 'named_numeric') {
        const n = parseInt(part, 10);
        if (Number.isNaN(n))
            return undefined;
        const internal = n - 1;
        return internal >= 0 && internal <= 5 ? internal : undefined;
    }
    const n = parseInt(part, 10);
    return Number.isNaN(n) ? undefined : n;
};
/**
 * Parse a rank string into levelIndex (1–11) and sublevel (0–5) without theme tier name tables.
 * Supports "N.M", leading level digit(s), and sublevel from trailing roman/numeric parts.
 */
export const parseRankStringToLevelSublevel = (rankString, config) => {
    if (!rankString)
        return { levelIndex: 1, sublevel: 0 };
    const s = rankString.trim();
    const c = config ?? getCurrentRankConfig();
    if (s.includes('.')) {
        const [a, b] = s.split('.');
        const levelIndex = Math.min(11, Math.max(1, parseInt(a ?? '1', 10) || 1));
        const sublevel = Math.min(5, Math.max(0, parseInt(b ?? '0', 10) || 0));
        return { levelIndex, sublevel };
    }
    const parts = s.split(/\s+/).filter(Boolean);
    const first = parts[0] ?? '';
    if (/^\d+$/.test(first)) {
        const levelIndex = Math.min(11, Math.max(1, parseInt(first, 10)));
        const sublevel = parseSublevelFromString(s, c) ?? 0;
        return { levelIndex, sublevel };
    }
    const fromSub = parseSublevelFromString(s, c);
    if (fromSub !== undefined && parts.length >= 2) {
        return { levelIndex: 1, sublevel: fromSub };
    }
    const anyDigit = s.match(/\b(?:[1-9]|1[01])\b/);
    if (anyDigit) {
        const idx = Math.min(11, Math.max(1, parseInt(anyDigit[0], 10)));
        return { levelIndex: idx, sublevel: 0 };
    }
    return { levelIndex: 1, sublevel: fromSub ?? 0 };
};
/**
 * Structural rank display for (levelIndex, sublevel) when API strings are not used.
 * Numeric themes: "N" or "N.M"; others: level index + formatted sublevel (no tier names from config).
 */
export const getFullRankDisplay = (levelIndex, sublevel, config) => {
    const c = config ?? getCurrentRankConfig();
    if (c.sublevelFormat === 'numeric') {
        return sublevel > 0 ? `${levelIndex}.${sublevel}` : String(levelIndex);
    }
    if (c.sublevelFormat === 'none') {
        return String(levelIndex);
    }
    return formatRankWithSublevel(levelIndex, sublevel, c);
};
/**
 * Format a rank string for display by parsing then applying structural rules (no theme name list).
 */
export const formatRankDisplayFromString = (rankString, config) => {
    const { levelIndex, sublevel } = parseRankStringToLevelSublevel(rankString, config);
    return getFullRankDisplay(levelIndex, sublevel, config);
};
/** 1–5 -> I, II, III, IV, V. */
export const convertToRoman = (num) => ROMAN_MAP[num] ?? String(num);
/** I, II, III, IV, V -> 1–5. */
export const convertFromRoman = (roman) => ROMAN_TO_NUM[roman.trim()] ?? 0;
/** Whether the current theme shows sublevels. */
export const hasSublevelDisplay = (config) => {
    const c = config ?? getCurrentRankConfig();
    return c.sublevelFormat !== 'none';
};
/**
 * Prefixes an API rank display string with the translated rank word + space when
 * `stateUi.vip.showRankPrefixBeforeDisplayName` is true (e.g. Fomo).
 */
export const formatRankDisplayWithOptionalRankPrefix = (label, translatedRankWord, showPrefix) => {
    const body = (label ?? '').trim();
    if (!body)
        return '';
    if (!showPrefix)
        return body;
    const word = translatedRankWord.trim();
    if (!word)
        return body;
    return `${word} ${body}`;
};
